import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, throwError } from 'rxjs';
import { ApiValidationError } from '../models';
import { LoggingService } from '../services/shared/logging/logging.service';

export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const toastr = inject(ToastrService);
  const loggingService = inject(LoggingService);

  // Check if this is an image check request (should suppress individual error toasts)
  const isImageCheckRequest = req.url.includes('.jpg') || req.headers.has('X-Skip-Error-Toast');

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        // Log error for debugging but don't trigger additional alerts
        loggingService.debug('ErrorInterceptor', `HTTP Error: ${error.status} ${error.statusText}`, error);

        // Skip toastr for image check requests
        if (isImageCheckRequest) {
          return throwError(() => error);
        }

        switch (error.status) {
          case 400: {
            const validationError = error.error as ApiValidationError;
            if (
              validationError?.errors &&
              Object.keys(validationError.errors).length > 0
            ) {
              // Format all validation errors
              const errorMessages = Object.entries(validationError.errors)
                .map(([field, details]) => {
                  // Convert camelCase to Title Case for field names
                  const formattedField = field
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (str) => str.toUpperCase());

                  // Handle both string and array error messages
                  const errorMessage = Array.isArray(details)
                    ? details.join(', ')
                    : typeof details === 'string'
                    ? details
                    : typeof details === 'object' && details !== null
                    ? Object.values(details).join(', ')
                    : 'Invalid value';

                  return `${formattedField}: ${errorMessage}`;
                })
                .join('\n');

              toastr.error(
                errorMessages,
                validationError.title || 'Validation Error'
              );
            } else {
              // Fallback for non-validation 400 errors
              toastr.error(error.error?.detail || 'Bad Request', 'Error');
            }
            break;
          }
          // case 401:
          //   toastr.error('Unauthorized. Please login again.');
          //   localStorage.removeItem('authToken');
          //   window.location.href = '/login';
          //   break;
          case 403:
            toastr.error(
              "Forbidden. You don't have permission to access this resource."
            );
            break;
          case 404:
            toastr.error('Resource not found.');
            break;
          case 408:
            toastr.error('Request timeout. Please try again.');
            break;
          case 500:
            toastr.error('Internal server error. Please try again later.');
            break;
          default:
            if (!navigator.onLine) {
              toastr.error('No Internet connection.', 'Error');
            } else {
              toastr.error(error.message, 'Error');
            }
        }
      }
      return throwError(() => error);
    })
  );
};
